import React, { useState, useEffect, useCallback } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Row, Col } from 'reactstrap';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import * as Icon from 'react-feather';
import { toast } from 'react-toastify';
import { useDebouncedCallback } from 'use-debounce';

import {
  queueAgentsGet,
  queueAvailableAgentsGet,
  queueAgentAdd,
  queueAgentRemove
} from '../../../store/slices/tenantSlices/incomingSlice';

// Manage the agents assigned to a single queue: list current (remove) + search/add.
const agentLabel = (a) =>
  a?.agent_name && a.agent_name !== 'Unknown' ? `${a.username} (${a.agent_name})` : a?.username;

const listBoxStyle = {
  height: 'calc(100vh - 250px)',
  overflowY: 'auto',
  border: '1px solid #e3e6ea',
  borderRadius: 6
};

const QueueAgentsModal = ({ isOpen, onCloseClick, queue }) => {
  const dispatch = useDispatch();
  const queueId = queue?.id;

  const [assigned, setAssigned] = useState([]);
  const [available, setAvailable] = useState([]);
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);

  const loadAssigned = useCallback(async () => {
    if (!queueId) return;
    try {
      const res = await dispatch(queueAgentsGet(queueId)).unwrap();
      setAssigned(res || []);
    } catch (e) {
      console.log('queue agents error', e);
    }
  }, [dispatch, queueId]);

  const loadAvailable = useCallback(
    async (s = '') => {
      if (!queueId) return;
      try {
        const res = await dispatch(queueAvailableAgentsGet({ queueId, search: s })).unwrap();
        setAvailable(res || []);
      } catch (e) {
        console.log('available agents error', e);
      }
    },
    [dispatch, queueId]
  );

  const debouncedAvailable = useDebouncedCallback((s) => loadAvailable(s), 350);

  useEffect(() => {
    if (isOpen && queueId) {
      setSearch('');
      loadAssigned();
      loadAvailable('');
    }
  }, [isOpen, queueId, loadAssigned, loadAvailable]);

  const handleAdd = async (userId) => {
    setBusyId(userId);
    try {
      await dispatch(queueAgentAdd({ queue_id: queueId, user_id: userId })).unwrap();
      toast.success('Agent added to queue');
      await loadAssigned();
      await loadAvailable(search);
    } catch (e) {
      toast.error('Failed to add agent');
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (userId) => {
    setBusyId(userId);
    try {
      await dispatch(queueAgentRemove({ queue_id: queueId, user_id: userId })).unwrap();
      toast.success('Agent removed from queue');
      await loadAssigned();
      await loadAvailable(search);
    } catch (e) {
      toast.error('Failed to remove agent');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onCloseClick} fullscreen scrollable>
      <ModalHeader toggle={onCloseClick}>
        Manage Agents{queue?.name ? ` — ${queue.name}` : ''}
      </ModalHeader>
      <ModalBody>
        <Row>
          {/* Current agents */}
          <Col md={6} className="mb-3">
            <div className="fw-bold mb-2">Agents in Queue ({assigned.length})</div>
            <div style={listBoxStyle}>
              {assigned.length === 0 ? (
                <div className="text-muted p-3">No agents in this queue</div>
              ) : (
                assigned.map((a) => (
                  <div
                    key={a.id}
                    className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
                    <span>{agentLabel(a)}</span>
                    <Button
                      color="link"
                      className="p-0 text-danger"
                      disabled={busyId === a.id}
                      onClick={() => handleRemove(a.id)}
                      title="Remove from queue">
                      <Icon.Trash2 size={16} />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </Col>

          {/* Add agents */}
          <Col md={6} className="mb-3">
            <div className="fw-bold mb-2">Add Agents</div>
            <input
              className="form-control mb-2"
              placeholder="Search agents..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                debouncedAvailable(e.target.value);
              }}
            />
            <div style={{ ...listBoxStyle, height: 'calc(100vh - 300px)' }}>
              {available.length === 0 ? (
                <div className="text-muted p-3">No matching agents</div>
              ) : (
                available.map((a) => (
                  <div
                    key={a.id}
                    className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
                    <span>{agentLabel(a)}</span>
                    <Button
                      color="link"
                      className="p-0 text-success"
                      disabled={busyId === a.id}
                      onClick={() => handleAdd(a.id)}
                      title="Add to queue">
                      <Icon.PlusCircle size={18} />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onCloseClick}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

QueueAgentsModal.propTypes = {
  isOpen: PropTypes.bool,
  onCloseClick: PropTypes.func,
  queue: PropTypes.object
};

export default QueueAgentsModal;
